import { Button, InputAdornment, OutlinedInput } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Else, If } from "react-if";
import { ReactComponent as SharedIcon } from "../../assets/mydashboard_shared_icon.svg";
import {
  useUsersSelectionItemStyles,
  useUsersSelectionTableStyles,
} from "./themes";

import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { DashboardMember } from "../../state/editorTypes";

type Props = {
  onSelectMember: (member: DashboardMember) => void;
  onRemoveMember: (member: DashboardMember) => void;
  onSelectAll: () => void;
  onRemoveAll: () => void;
  setSelectedItems: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => void;
  selectedItems: DashboardMember[];
  allItems: DashboardMember[];
  columnOneTitle: string;
  columnTwoTitle: string;
  columnOneWithSearch?: boolean;
  columnTwoWithSearch?: boolean;
  columnOneSearchPlaceholder?: string;
  columnTwoSearchPlaceholder?: string;
  isGroup?: boolean;
};

const SelectionItem = ({
  type,
  item,
  handler,
  withImage,
}: {
  type: "listItem" | "selectedItem";
  item: any;
  handler: (member: DashboardMember) => void;
  withImage?: boolean;
}) => {
  const { t } = useTranslation();
  const classes = useUsersSelectionItemStyles(true);
  return (
    <div className={classes.container}>
      <div className={classes.itemInfo}>
        {withImage && (
          <div className={classes.imagePlaceholder}>
            {item.name
              .split(" ")
              .map((word: string, idx: number) => (idx < 2 ? word[0] : null))
              .join("")}
          </div>
        )}
        <p className={classes.itemText}>{`${item.fullname} (${item.name})`}</p>
      </div>
      {type === "listItem" ? (
        <Button
          onClick={() => handler(item)}
          className={classes.addIcon}
          aria-hidden
        >
          {t("Share")}
        </Button>
      ) : (
        <Button
          onClick={() => handler(item)}
          className={classes.removeIcon}
          aria-hidden
        >
          {t("UnShare")}
        </Button>
      )}
    </div>
  );
};
const SelectionList = ({
  handler,
  title,
  type,
  searchPlaceHolder,
  withSearch,
  itemsList,
  isGroup,
  children,
  placeHolder,
  onChange,
}: {
  handler: (member: DashboardMember) => void;
  withSearch?: boolean;
  title: string;
  type: "listItem" | "selectedItem";
  searchPlaceHolder: string;
  itemsList: DashboardMember[];
  isGroup?: boolean;
  children: any;
  /** react component to be rendered if list is empty */
  placeHolder?: JSX.Element;
  onChange: (searchValue: string) => void;
}) => {
  const ctx = useAppContext();
  const classes = useUsersSelectionTableStyles(ctx.rtl)();
  const { t } = useTranslation();

  return (
    <div className={classes.column}>
      <div className={classes.columnHeader}>
        <p className={classes.columnTitle}>{title}</p>
        {withSearch ? (
          <OutlinedInput
            onChange={(ev) => onChange(ev.currentTarget.value)}
            className={classes.searchField}
            fullWidth
            placeholder={searchPlaceHolder}
            endAdornment={
              <InputAdornment style={{ marginLeft: 0 }} position="end">
                <SearchIcon
                  fontSize="large"
                  className={classes.searchIcon}
                  aria-hidden="true"
                />
              </InputAdornment>
            }
          />
        ) : (
          <hr className={classes.horizontalSeparator} />
        )}
      </div>
      <div className={classes.columnContent}>
        <If condition={itemsList.length === 0 && placeHolder !== undefined}>
          {placeHolder}
          <Else>
            {children}
            {itemsList.map((item, idx) => (
              <SelectionItem
                key={item.id}
                handler={handler}
                item={item}
                type={type}
                withImage={!isGroup}
              />
            ))}
          </Else>
        </If>
      </div>
    </div>
  );
};
const UsersSelection = (props: Props) => {
  const ctx = useAppContext();
  const classes = useUsersSelectionTableStyles(ctx.rtl)();
  const { t } = useTranslation();

  const EmptySharedWithPlaceHolder = () => {
    return (
      <div className={classes.emptyPlaceHolder}>
        <SharedIcon />
        {t("No invited users")}
      </div>
    );
  };

  const {
    setSelectedItems,
    selectedItems = [],
    allItems = [],
    columnOneTitle,
    columnTwoTitle,
    columnOneWithSearch = true,
    columnTwoWithSearch = true,
    columnTwoSearchPlaceholder = `${t("search for name")}...`,
    columnOneSearchPlaceholder = `${t("search for name")}...`,
  } = props;

  const selectedIds = React.useMemo(() => {
    return props.selectedItems.map((item) => item.id);
  }, [props.selectedItems]);

  const [itemsList, setItemsList] = useState<DashboardMember[]>(allItems);
  const [searchTextAllUsers, setsearchTextAllUsers] = useState("");
  const [searchTextSharedUsers, setsearchTextSharedUsers] = useState("");
  return (
    <div className={classes.container}>
      <SelectionList
        onChange={(val) => setsearchTextAllUsers(val.toLocaleLowerCase())}
        handler={props.onSelectMember}
        itemsList={itemsList
          .filter((item) => !selectedIds.includes(item.id))
          .filter((item) =>
            item.name.toLocaleLowerCase().includes(searchTextAllUsers)
          )}
        title={columnOneTitle}
        searchPlaceHolder={columnOneSearchPlaceholder}
        withSearch={columnOneWithSearch}
        type="listItem"
      >
        {/* <Button
          disabled={itemsList.length === 0}
          className={classes.doAllBtn}
          onClick={props.onSelectAll}
          aria-hidden
        >
          {t('Share to all members')}
        </Button> */}
      </SelectionList>

      <div className={classes.verticalSeparator} />
      <SelectionList
        onChange={(val) => setsearchTextSharedUsers(val.toLocaleLowerCase())}
        handler={props.onRemoveMember}
        itemsList={selectedItems.filter((item) =>
          item.name.toLocaleLowerCase().includes(searchTextSharedUsers)
        )}
        type="selectedItem"
        title={columnTwoTitle}
        searchPlaceHolder={columnTwoSearchPlaceholder}
        withSearch={columnTwoWithSearch}
        placeHolder={<EmptySharedWithPlaceHolder />}
      >
        <Button
          disabled={selectedItems.length === 0}
          className={classes.doAllBtn}
          onClick={props.onRemoveAll}
          aria-hidden
        >
          {t("Unshare to all members")}
        </Button>
      </SelectionList>
    </div>
  );
};

export default UsersSelection;
